/**
 * Auswerten von Daten: AutoFilter, PivotTables und Datenschnitte.
 *
 * Eigener Abruf, weil diese Angaben aus deutlich höheren Anforderungssätzen
 * stammen als die Grundmomentaufnahme: der Aufbau einer PivotTable ab ExcelApi
 * 1.8, der AutoFilter eines Blattes ab 1.9, Datenschnitte ab 1.10.
 */

import { excelApi, supportsExcelApi } from "./capabilities";
import { ExcelUnavailableError, isExcelAvailable, splitAddress } from "./workbook";

export interface AutoFilterInfo {
  sheetName: string;
  /** Ist überhaupt ein AutoFilter gesetzt? */
  enabled: boolean;
  /** Ist gerade ein Filterkriterium aktiv, wird also etwas ausgeblendet? */
  isDataFiltered: boolean | null;
  /** Bereich, über den der Filter liegt. */
  address: string | null;
}

export interface PivotHierarchyInfo {
  name: string;
  /** Nullbasierte Position innerhalb ihres Feldbereichs. */
  position: number | null;
}

export interface PivotTableInfo {
  id: string;
  name: string;
  /** Blatt, auf dem die PivotTable ausgegeben wird. */
  sheetName: string;
  /** Felder im Zeilenbereich. `null` vor ExcelApi 1.8. */
  rowFields: PivotHierarchyInfo[] | null;
  /** Felder im Spaltenbereich. `null` vor 1.8. */
  columnFields: PivotHierarchyInfo[] | null;
  /** Felder im Wertebereich. `null` vor 1.8. */
  dataFields: PivotHierarchyInfo[] | null;
  /** Felder im Filterbereich. `null` vor 1.8. */
  filterFields: PivotHierarchyInfo[] | null;
  /** Berichtslayout: "Compact", "Outline" oder "Tabular". `null` vor 1.8. */
  layoutType: string | null;
  /** Ausgabebereich der PivotTable. */
  address: string | null;
}

export interface SlicerInfo {
  id: string;
  name: string;
  /** Beschriftung, die über dem Datenschnitt steht. */
  caption: string | null;
  sheetName: string | null;
  /** Anzahl der gerade ausgewählten Einträge. */
  selectedCount: number | null;
  /** Gesamtzahl der Einträge im Datenschnitt. */
  itemCount: number | null;
}

export interface DataSnapshot {
  /** AutoFilter je Blatt. `null` vor ExcelApi 1.9. */
  autoFilters: AutoFilterInfo[] | null;
  /** PivotTables der Arbeitsmappe. `null` vor 1.3. */
  pivotTables: PivotTableInfo[] | null;
  /** Datenschnitte der Arbeitsmappe. `null` vor 1.10. */
  slicers: SlicerInfo[] | null;
}

function value<T>(read: () => T): T | null {
  try {
    const result = read();
    return result === undefined ? null : result;
  } catch {
    return null;
  }
}

function text(read: () => unknown): string | null {
  const result = value(read);
  return typeof result === "string" ? result : null;
}

function flag(read: () => unknown): boolean | null {
  const result = value(read);
  return typeof result === "boolean" ? result : null;
}

function zahl(read: () => unknown): number | null {
  const result = value(read);
  return typeof result === "number" && Number.isFinite(result) ? result : null;
}

function hierarchien(
  sammlung: { items: Array<{ name: string; position?: number }> } | null,
): PivotHierarchyInfo[] | null {
  if (!sammlung) return null;
  const items = value(() => sammlung.items);
  if (!Array.isArray(items)) return null;
  return items.map((eintrag, index) => ({
    name: text(() => eintrag.name) ?? "",
    position: zahl(() => eintrag.position) ?? index,
  }));
}

/** Liest AutoFilter, PivotTables und Datenschnitte der Arbeitsmappe. */
export async function readData(): Promise<DataSnapshot> {
  if (!isExcelAvailable()) throw new ExcelUnavailableError();

  const canReadAutoFilter = supportsExcelApi(excelApi.autofilter);
  const canReadPivot = supportsExcelApi(excelApi.tabellendetails);
  const canReadPivotLayout = supportsExcelApi(excelApi.pivotaufbau);
  const canReadSlicers = supportsExcelApi(excelApi.datenschnitte);

  return Excel.run(async (context) => {
    const sheetCollection = context.workbook.worksheets;
    sheetCollection.load("items/name");

    const pivotCollection = canReadPivot ? context.workbook.pivotTables : null;
    if (pivotCollection) pivotCollection.load("items/id,items/name");

    const slicerCollection = canReadSlicers ? context.workbook.slicers : null;
    if (slicerCollection) slicerCollection.load("items/id,items/name,items/caption");

    await context.sync();

    const sheets = sheetCollection.items;

    // Stufe 2: Einzelheiten, die je Objekt einen eigenen Ladeschritt brauchen.
    const filterRequests: Array<{ sheetName: string; filter: Excel.AutoFilter; range: Excel.Range }> = [];
    if (canReadAutoFilter) {
      for (const sheet of sheets) {
        const filter = sheet.autoFilter;
        filter.load("enabled,isDataFiltered");
        const range = filter.getRangeOrNullObject();
        range.load("address,isNullObject");
        filterRequests.push({ sheetName: text(() => sheet.name) ?? "", filter, range });
      }
    }

    const pivotRequests: Array<{
      pivot: Excel.PivotTable;
      layout: Excel.PivotLayout | null;
      bereich: Excel.Range | null;
      blatt: Excel.Worksheet;
    }> = [];
    if (pivotCollection) {
      for (const pivot of pivotCollection.items) {
        const blatt = pivot.worksheet;
        blatt.load("name");
        let layout: Excel.PivotLayout | null = null;
        let bereich: Excel.Range | null = null;
        if (canReadPivotLayout) {
          pivot.rowHierarchies.load("items/name,items/position");
          pivot.columnHierarchies.load("items/name,items/position");
          pivot.dataHierarchies.load("items/name,items/position");
          pivot.filterHierarchies.load("items/name,items/position");
          layout = pivot.layout;
          layout.load("layoutType");
          bereich = layout.getRange();
          bereich.load("address");
        }
        pivotRequests.push({ pivot, layout, bereich, blatt });
      }
    }

    const slicerRequests: Array<{ slicer: Excel.Slicer; blatt: Excel.Worksheet; items: Excel.SlicerItemCollection }> = [];
    if (slicerCollection) {
      for (const slicer of slicerCollection.items) {
        const blatt = slicer.worksheet;
        blatt.load("name");
        const items = slicer.slicerItems;
        items.load("items/isSelected");
        slicerRequests.push({ slicer, blatt, items });
      }
    }

    let gelesen = true;
    try {
      await context.sync();
    } catch {
      gelesen = false;
    }

    if (!gelesen) {
      return { autoFilters: null, pivotTables: null, slicers: null };
    }

    const autoFilters: AutoFilterInfo[] | null = canReadAutoFilter
      ? filterRequests.map((request) => ({
          sheetName: request.sheetName,
          enabled: flag(() => request.filter.enabled) ?? false,
          isDataFiltered: flag(() => request.filter.isDataFiltered),
          address:
            value(() => request.range.isNullObject) !== true
              ? splitAddress(text(() => request.range.address) ?? "").range || null
              : null,
        }))
      : null;

    const pivotTables: PivotTableInfo[] | null = pivotCollection
      ? pivotRequests.map((request) => ({
          id: text(() => request.pivot.id) ?? "",
          name: text(() => request.pivot.name) ?? "",
          sheetName: text(() => request.blatt.name) ?? "",
          rowFields: canReadPivotLayout ? hierarchien(request.pivot.rowHierarchies) : null,
          columnFields: canReadPivotLayout ? hierarchien(request.pivot.columnHierarchies) : null,
          dataFields: canReadPivotLayout ? hierarchien(request.pivot.dataHierarchies) : null,
          filterFields: canReadPivotLayout ? hierarchien(request.pivot.filterHierarchies) : null,
          layoutType: request.layout ? text(() => request.layout?.layoutType) : null,
          address: request.bereich
            ? splitAddress(text(() => request.bereich?.address) ?? "").range || null
            : null,
        }))
      : null;

    const slicers: SlicerInfo[] | null = slicerCollection
      ? slicerRequests.map((request) => {
          const items = value(() => request.items.items);
          return {
            id: text(() => request.slicer.id) ?? "",
            name: text(() => request.slicer.name) ?? "",
            caption: text(() => request.slicer.caption),
            sheetName: text(() => request.blatt.name),
            selectedCount: Array.isArray(items)
              ? items.filter((eintrag) => flag(() => eintrag.isSelected) === true).length
              : null,
            itemCount: Array.isArray(items) ? items.length : null,
          };
        })
      : null;

    return { autoFilters, pivotTables, slicers };
  });
}

/* ---------- Auswertung ---------- */

/** Blätter, auf denen ein AutoFilter gesetzt ist. */
export function blaetterMitFilter(snapshot: DataSnapshot): AutoFilterInfo[] {
  return (snapshot.autoFilters ?? []).filter((eintrag) => eintrag.enabled);
}

/** Ist irgendwo in der Arbeitsmappe gerade ein Filter aktiv? */
export function filtertGerade(snapshot: DataSnapshot): boolean {
  return (snapshot.autoFilters ?? []).some((eintrag) => eintrag.isDataFiltered === true);
}

/** Hat die PivotTable mindestens ein Feld im Zeilen- und im Wertebereich? */
export function istAufgebaut(pivot: PivotTableInfo): boolean {
  return (pivot.rowFields?.length ?? 0) > 0 && (pivot.dataFields?.length ?? 0) > 0;
}
